import { useState } from 'react';
import { useJobTaskApi } from '../../../../../clients/quipli-service/job-tasks/job-task-api';
import { JobType } from '../../../../../clients/quipli-service/job-tasks/job-tasks';
import { InspectionModalNav, isTaskComplete, TaskProps } from '../../inspection-modal/inspection-modal';
import { toast } from '../../../../../util/toasts';
import { Typography } from '../../../../../components/typography/typography';
import { InspectionUnitHeader } from '../inspection-header';
import { Field } from '../../../../../components/field/field';
import '../../inspection-modal/inspection-modal.scss';

/**
 * Hours and fuel task.
 * @param task - The task to inspect.
 * @param jobId - The job id.
 * @param jobType - The job type.
 * @param product - The product to inspect.
 * @param unit - The unit to inspect.
 * @param unitTrackingVia - The unit tracking via to use.
 * @param fuelUnit - The fuel unit to use.
 * @param next - The next function.
 * @param back - The back function.
 * @param isPreview - Whether the task is in preview mode (in the edit view)
 * @param setJob - The function to set the job.
 * @param isFirstTask - Whether this is the first task in the inspection.
 * @param isLastTask - Whether this is the last task in the inspection.
 */
export const HoursFuelTask = ({
  task,
  jobId,
  jobType,
  product,
  unit,
  unitTrackingVia,
  fuelUnit,
  next,
  back,
  isPreview = false,
  setJob,
  isFirstTask = false,
  isLastTask = false,
}: TaskProps & { jobType: JobType; fuelUnit: 'gallons' | 'liters' }) => {
  const { updateTask } = useJobTaskApi();
  const [hours, setHours] = useState(task.metadata?.hours ?? unit.lastHourReading?.hour);
  const [fuel, setFuel] = useState(task.metadata?.fuel);

  const saveTask = async (forward: boolean) => {
    // TODO: clean up redundancy...
    if (isPreview || !task || !task.metadata) return;
    try {
      const updatedJob = await updateTask(jobId, task.id, isTaskComplete(task, product), {
        ...task.metadata,
        hours,
        fuel,
      });
      setJob(updatedJob);
      if (forward) next();
      else back();
    } catch (e) {
      console.error(e);
      toast('Error updating task', 'error');
    }
  };

  return (
    <div className="machine-hours-task-wrapper">
      <Typography className="text-center" type="text-lg">
        {task.header}
      </Typography>
      <InspectionUnitHeader product={product} unitNumber={unit[unitTrackingVia]} />
      <Typography type="text-sm" className="text-grey-500 mb-2">
        {task.instructions}
      </Typography>
      <div className="machine-hours-input-wrapper mb-2">
        {product.allowHourTracking && (
          <div className="input-wrapper">
            <Field
              label="Machine Hours"
              type="number"
              value={hours}
              onChange={(e) => setHours(e)}
              className="border border-grey-200 rounded p-2 w-full"
            />
          </div>
        )}
        {jobType === JobType.CheckinInspection && product.fuelChargeProductId && (
          <div className="input-wrapper">
            <Field
              label={`Fuel (${fuelUnit})`}
              type="number"
              value={fuel}
              onChange={(e) => setFuel(e)}
              className="border border-grey-200 rounded p-2 w-full"
            />
          </div>
        )}
        {!((jobType === JobType.CheckinInspection && product.fuelChargeProductId) || product.allowHourTracking) && (
          <Typography type="text-sm" className="text-grey-500 mb-2">
            No fuel or hour tracking required for this unit.
          </Typography>
        )}
      </div>
      {!isPreview && <InspectionModalNav saveTask={saveTask} isFirstStep={isFirstTask} isLastStep={isLastTask} />}
    </div>
  );
};
